/* ============================================================
   YOLÈTE — Logique partagée (panier, drawer, header, reveal)
   Chargé sur toutes les pages, après data.js.
   ============================================================ */

/* ---------- Pastilles de coloris des boucles ----------
   Une boucle fine, zoomée dans une pastille ronde, donne surtout du fond blanc.
   On affiche donc la matière plutôt que la photo : métal en dégradé, émaux à plat.
   Les tiges gardent leur zoom photo, qui montre le grain du cuir.
   Clé = le libellé du coloris dans BOUCLE_SWATCHES. Les coloris nommés
   « Coloris » n'ont aucune information de couleur, ils gardent la photo. */
const BOUCLE_DOT = {
  'Argenté':   'linear-gradient(145deg,#FDFDFC 0%,#D7DADE 26%,#9AA0A6 56%,#C8CCD0 76%,#7C838A 100%)',
  'Doré':      'linear-gradient(145deg,#FBF1CC 0%,#E4C263 28%,#B58A24 58%,#EAD08A 78%,#96701A 100%)',
  /* libellés utilisés par les cartes boutique (dossier client) */
  'Argent':    'linear-gradient(145deg,#FDFDFC 0%,#D7DADE 26%,#9AA0A6 56%,#C8CCD0 76%,#7C838A 100%)',
  'Or':        'linear-gradient(145deg,#FBF1CC 0%,#E4C263 28%,#B58A24 58%,#EAD08A 78%,#96701A 100%)',
  'Or / Argent': 'linear-gradient(115deg,#FBF1CC 0%,#D9B455 42%,#9AA0A6 58%,#EDEFF1 100%)',
  'Verte':     'linear-gradient(145deg,#5E8C63 0%,#3D6B45 50%,#24462C 100%)',
  'Vert':      'linear-gradient(145deg,#5E8C63 0%,#3D6B45 50%,#24462C 100%)',
  'Glitter':   'linear-gradient(145deg,#FFF6D8 0%,#E9C96E 30%,#C79A28 55%,#F5E3A6 72%,#A87F1E 100%)',
  'Ambré':     'linear-gradient(145deg,#F4D6A2 0%,#D89A45 32%,#A8641F 66%,#C98A3A 100%)',
  'Ivoire':    'linear-gradient(145deg,#FBF6EC 0%,#EDE3D0 45%,#D9CCB4 100%)',
  'Noir':      'linear-gradient(145deg,#3A3530 0%,#1E1B18 45%,#0E0C0A 100%)',
  'Rouge':     'linear-gradient(145deg,#9B3033 0%,#7E1F22 50%,#571013 100%)',
  'Marron':    'linear-gradient(145deg,#7A5236 0%,#5A3A24 50%,#3B2515 100%)',
  'Bleu nuit': 'linear-gradient(145deg,#33445F 0%,#22304A 50%,#151F31 100%)',
  'Bleu':      'linear-gradient(145deg,#3D63A0 0%,#2B4A7A 50%,#1B3054 100%)',
  'Camel':     'linear-gradient(145deg,#CFA36B 0%,#B0854F 50%,#8A6436 100%)',
  'Kaki':      'linear-gradient(145deg,#84845A 0%,#6B6B45 50%,#4B4B2E 100%)'
};
function boucleDotStyle(label) {
  return (label && BOUCLE_DOT[label]) || null;
}
/* Pastilles d'un jeu de coloris. Une pastille n'est retenue que si sa couleur est
   connue ET si son libellé est unique dans la fiche : certaines fiches nomment deux
   coloris différents « Noir », deux pastilles identiques rendraient le choix impossible.
   Renvoie un tableau aligné sur ways, avec null là où il faut garder la photo. */
function boucleDotSet(ways) {
  const n = {};
  ways.forEach(w => { n[w.label] = (n[w.label] || 0) + 1; });
  return ways.map(w => (n[w.label] === 1 ? boucleDotStyle(w.label) : null));
}

/* ---------- Pastilles standard des finitions Shopify (24/09/2026) ----------
   Une boucle ajoutée depuis Shopify n'a pas d'entrée dans boucles-photos.js. Ses
   pastilles viennent alors de l'option Couleur de ses variantes, lue mot à mot :
   un métal (GOLD, SILVER...) puis, éventuellement, des pierres ou un émail
   (TOPAZE, BLACK...). Les teintes de métal sont les médianes des pastilles des
   55 boucles du site, pour qu'une nouvelle boucle ressemble aux autres.
   Référence pour Charlotte : documents/charlotte/pastilles-boucles.pdf, générée
   depuis ce bloc par scripts/pastilles_pdf.mjs. DEBUT-PALETTE */
const PASTILLE = (a, b, c) => `linear-gradient(145deg,${a} 0%,${b} 48%,${c} 100%)`;
/* Métaux, du plus long au plus court : « OLD GOLD » doit passer avant « GOLD ». */
const FINITION_METAUX = [
  ['ANTIC SILVER', 'Vieil argent', PASTILLE('rgb(206,203,197)', 'rgb(152,148,142)', 'rgb(94,91,87)')],
  ['OLD SILVER',   'Vieil argent', PASTILLE('rgb(206,203,197)', 'rgb(152,148,142)', 'rgb(94,91,87)')],
  ['ANTIC GOLD',   'Or',           PASTILLE('rgb(220,194,149)', 'rgb(172,154,109)', 'rgb(110,98,69)')],
  ['OLD GOLD',     'Or',           PASTILLE('rgb(220,194,149)', 'rgb(172,154,109)', 'rgb(110,98,69)')],
  ['BLACK MAT',    'Noir mat',     PASTILLE('rgb(72,68,64)', 'rgb(42,40,38)', 'rgb(20,19,18)')],
  ['GOLD',         'Or',           PASTILLE('rgb(220,194,149)', 'rgb(172,154,109)', 'rgb(110,98,69)')],
  ['SILVER',       'Argent',       PASTILLE('rgb(236,231,223)', 'rgb(181,176,169)', 'rgb(115,112,108)')],
];
/* Pierres, émaux et couleurs. Clé = le mot saisi dans Shopify. */
const FINITION_TEINTES = {
  BLACK:      ['noir',       PASTILLE('#3A3530', '#1E1B18', '#0E0C0A')],
  NOIR:       ['noir',       PASTILLE('#3A3530', '#1E1B18', '#0E0C0A')],
  STRASS:     ['strass',     PASTILLE('#FFFFFF', '#DDE3EA', '#A9B3BF')],
  CRISTAL:    ['cristal',    PASTILLE('#FFFFFF', '#DDE3EA', '#A9B3BF')],
  IVOIRE:     ['ivoire',     PASTILLE('#FBF6EC', '#EDE3D0', '#D9CCB4')],
  CREAM:      ['crème',      PASTILLE('#FBF6EC', '#EDE3D0', '#D9CCB4')],
  NACRE:      ['nacre',      PASTILLE('#FFFDF8', '#EDE6F0', '#CFC8D6')],
  WHITE:      ['blanc',      PASTILLE('#FFFFFF', '#F1F1EF', '#D6D6D2')],
  TOPAZE:     ['topaze',     PASTILLE('#F4D6A2', '#D89A45', '#A8641F')],
  TURQUOISE:  ['turquoise',  PASTILLE('#7FD0CB', '#3FA7A2', '#1F6F6B')],
  EMERAUDE:   ['émeraude',   PASTILLE('#5FB38A', '#2E8A5E', '#17583A')],
  JADE:       ['jade',       PASTILLE('#A9D3B5', '#6FA886', '#3F7457')],
  OLIVE:      ['olive',      PASTILLE('#9EA25A', '#767A36', '#4E5220')],
  KAKI:       ['kaki',       PASTILLE('#84845A', '#6B6B45', '#4B4B2E')],
  SAPHIR:     ['saphir',     PASTILLE('#4F6FC4', '#2E4C9C', '#1A2F66')],
  BLEU:       ['bleu',       PASTILLE('#3D63A0', '#2B4A7A', '#1B3054')],
  BLUE:       ['bleu',       PASTILLE('#3D63A0', '#2B4A7A', '#1B3054')],
  NAVY:       ['bleu nuit',  PASTILLE('#33445F', '#22304A', '#151F31')],
  RUBIS:      ['rubis',      PASTILLE('#C8344A', '#9C1B2E', '#63101C')],
  RED:        ['rouge',      PASTILLE('#9B3033', '#7E1F22', '#571013')],
  CHERRY:     ['cerise',     PASTILLE('#B8323F', '#8E1E2B', '#5E1119')],
  BURGUNDY:   ['bordeaux',   PASTILLE('#962832', '#6E1622', '#460E16')],
  ROSE:       ['rose',       PASTILLE('#F2B8C6', '#DE8AA3', '#B85E7C')],
  PINK:       ['rose',       PASTILLE('#F2B8C6', '#DE8AA3', '#B85E7C')],
  FUSHIA:     ['fuchsia',    PASTILLE('#E0529A', '#B82C74', '#7E1850')],
  VIOLINE:    ['violine',    PASTILLE('#9B3F8C', '#72266A', '#4A1545')],
  PERVENCHE:  ['pervenche',  PASTILLE('#A8B4E8', '#7F8FD6', '#5566B0')],
  YELLOW:     ['jaune',      PASTILLE('#F7DF7A', '#E3BE3A', '#B08E1C')],
  BROWN:      ['marron',     PASTILLE('#7A5236', '#5A3A24', '#3B2515')],
  COCOA:      ['cacao',      PASTILLE('#78563C', '#4E3424', '#301F14')],
  ACAJOU:     ['acajou',     PASTILLE('#A5552F', '#7C3A1C', '#4F220F')],
  CAMEL:      ['camel',      PASTILLE('#CFA36B', '#B0854F', '#8A6436')],
  TAUPE:      ['taupe',      PASTILLE('#A89A8C', '#857668', '#5E5247')],
  CORNE:      ['corne',      PASTILLE('#6B5A48', '#3E3228', '#1F1813')],
  ANTHRACITE: ['anthracite', PASTILLE('#5A5D62', '#3C3F44', '#24262A')],
  TACHETE:    ['tacheté',    'radial-gradient(circle at 32% 34%,#5A5550 0 9%,transparent 10%),radial-gradient(circle at 66% 60%,#5A5550 0 8%,transparent 9%),radial-gradient(circle at 42% 74%,#5A5550 0 7%,transparent 8%),linear-gradient(145deg,#E3E0DA,#B9B4AC)'],
  GLITTER:    ['glitter',    PASTILLE('#FFF6D8', '#E9C96E', '#A87F1E')],
  LEOPARD:    ['léopard',    'radial-gradient(circle at 30% 32%,#3A2A1A 0 11%,transparent 12%),radial-gradient(circle at 68% 58%,#3A2A1A 0 10%,transparent 11%),radial-gradient(circle at 40% 76%,#3A2A1A 0 8%,transparent 9%),linear-gradient(145deg,#E6BD78,#C08A3E)'],
  MIXT:       ['multicolore', 'conic-gradient(#C8344A,#E3BE3A,#3FA7A2,#2E4C9C,#C8344A)'],
  MULTI:      ['multicolore', 'conic-gradient(#C8344A,#E3BE3A,#3FA7A2,#2E4C9C,#C8344A)'],
};
/* Teintes en deux mots, ramenées à leur mot de la palette avant lecture. */
const FINITION_ALIAS = [['BLEU NUIT', 'NAVY']];
/* Mots qui précisent sans donner de couleur : on les écarte du nom affiché. */
const FINITION_NEUTRES = new Set(['PIERRES', 'PIERRE', 'STONE', 'STONES', '&', 'ET', 'AND']);
/* FIN-PALETTE */

/* « OLD GOLD / TOPAZE » -> { metal: ['Or', dégradé], teintes: [['topaze', dégradé]], label: 'Or, topaze' } */
function finitionShopify(valeur) {
  let v = ' ' + String(valeur || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[\/,\-]+/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
  FINITION_ALIAS.forEach(([de, vers]) => { v = v.split(' ' + de + ' ').join(' ' + vers + ' '); });
  let metal = null;
  for (const [mot, label, dot] of FINITION_METAUX) {
    if (v.indexOf(' ' + mot + ' ') !== -1) { metal = [label, dot]; v = v.replace(' ' + mot + ' ', ' '); break; }
  }
  /* Les mots gardent leur ordre : connus, ils sont traduits ; inconnus (un nom de
     modèle comme POUSSIN ou FERRARI), ils restent tels quels, en minuscules. */
  const teintes = [], mots = [];
  let email = false;
  v.trim().split(' ').filter(Boolean).forEach(mot => {
    if (mot === 'EMAIL') { email = true; return; }
    if (FINITION_NEUTRES.has(mot)) return;
    const t = FINITION_TEINTES[mot];
    if (t) { const nom = (email ? 'émail ' : '') + t[0]; teintes.push([nom, t[1]]); mots.push(nom); email = false; }
    else mots.push(mot.toLowerCase());
  });
  let label = metal ? metal[0] + (mots.length ? ', ' + mots.join(' ') : '') : mots.join(' ');
  label = label ? label.charAt(0).toUpperCase() + label.slice(1) : String(valeur || '');
  return { metal: metal, teintes: teintes, label: label };
}
window.finitionShopify = finitionShopify;

/* Pastilles d'une boucle lues dans ses variantes Shopify. Même forme que
   BOUCLE_SWATCHES, pour passer partout où celles-ci passent.
   La pastille montre la pierre quand toutes les finitions ont le même métal (N214,
   Topaze et Noir), ou quand une autre finition partage le sien (N405 : or topaze et
   or émeraude, argent noir), sinon le métal (N331, argent et or). */
function swatchesShopify(p) {
  if (!p || p.type !== 'boucle' || !Array.isArray(p.variants)) return null;
  const vus = new Map();
  p.variants.forEach(v => {
    const o = String((v && v.option1) || '').trim();
    if (!o || /^default title$/i.test(o) || vus.has(o)) return;
    vus.set(o, v);
  });
  if (!vus.size) return null;
  const vals = [...vus.keys()];
  const lus = vals.map(finitionShopify);
  const metal = f => (f.metal ? f.metal[0] : '');
  const memeMetal = new Set(lus.map(metal)).size === 1;
  const parMetal = {};
  lus.forEach(f => { parMetal[metal(f)] = (parMetal[metal(f)] || 0) + 1; });
  const swatches = vals.map((o, i) => {
    const f = lus[i], v = vus.get(o);
    const dot = pastilleShopify(p, o)
      || ((memeMetal || parMetal[metal(f)] > 1) && f.teintes.length ? f.teintes[0][1] : (f.metal ? f.metal[1] : (f.teintes[0] || [])[1])) || null;
    const photo = (v && v.image) || p.img;
    return { label: f.label, voption: o, photo: photo, img: photo, dotimg: photo, dot: dot, deShopify: true };
  });
  return { default_photo: p.img, swatches: swatches, deShopify: true };
}
window.swatchesShopify = swatchesShopify;

/* Sur la boutique, les finitions d'une boucle sont ses variantes Shopify, avec la photo de
   chaque variante et les photos de la fiche (21/10/2026) : une finition ajoutée par
   Charlotte s'affiche aussitôt. Les tables du site (cut-swatches.js) ne servent plus qu'à
   la copie de travail ; swatchFor, findWays et les vignettes passent alors par
   swatchesShopify. */
if (window.SHOP) delete window.BOUCLE_SWATCHES;

/* Pastille choisie dans Shopify pour une valeur de l'option Couleur, quand l'option
   est reliée aux couleurs Shopify (snippet yolete-product-json, p.pastilles) : l'image
   de la couleur si elle en a une, sinon sa teinte. Elle prime sur la pastille déduite
   du nom, qui retombait sur le métal quand la pierre était inconnue (« GOLD/COBALT »
   donnait Or). Sans pastille dans Shopify, rien ne change (9 oct 2026). */
function pastilleShopify(p, option) {
  const P = p && p.pastilles;
  if (!P || !option) return null;
  const cle = s => String(s || '').toUpperCase().replace(/\s*\/\s*/g, '/').replace(/\/\d+\s*MM$/, '').replace(/\s+/g, ' ').trim();
  let x = P[option];
  if (!x) { const k = cle(option); for (const n in P) if (cle(n) === k) { x = P[n]; break; } }
  if (!x) return null;
  if (x.i) return `url('${x.i}') center / cover no-repeat`;
  return x.c ? pastilleTeinte(x.c) : null;
}
window.pastilleShopify = pastilleShopify;
/* Teinte unie choisie dans Shopify, ombrée comme les pastilles du site (PASTILLE) :
   toutes les boucles étant reliées aux couleurs Shopify, leurs pastilles gardent le
   relief qu'avaient les pastilles déduites du nom (28/10/2026). */
function pastilleTeinte(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16), c = [n >> 16, (n >> 8) & 255, n & 255];
  const vers = (cible, f) => 'rgb(' + c.map(v => Math.round(v + (cible - v) * f)).join(',') + ')';
  return PASTILLE(vers(255, 0.35), 'rgb(' + c.join(',') + ')', vers(0, 0.36));
}

/* Boucle préparée (boucles-photos.js) : chaque pastille qui porte l'option Shopify
   exacte (voption, comme la N255) prend la pastille choisie dans Shopify. */
function avecPastillesShopify(p, swatches) {
  if (!p || !p.pastilles || !Array.isArray(swatches)) return swatches;
  return swatches.map(s => {
    const d = s && s.voption && pastilleShopify(p, s.voption);
    return d ? Object.assign({}, s, { dot: d }) : s;
  });
}
window.avecPastillesShopify = avecPastillesShopify;

/* ---------- Nouveautés (24/09/2026) ----------
   Sur la boutique, une pièce est « nouvelle » si elle est dans la collection
   Shopify « Nouveautés » (manuelle, cochée dans la fiche). Avant, c'était la
   liste figée des photos de la campagne d'été (40 boucles), posée par
   catalog-enrich.js : elle ne sert plus que sur la copie de travail. Ce fichier
   se charge après tous les catalogues (boucles, maroquinerie), avant les pages. */
(function marquerNouveautes() {
  if (typeof PRODUCTS === 'undefined' || !window.SHOP || !Array.isArray(SHOP.nouveautes)) return;
  const N = new Set(SHOP.nouveautes);
  PRODUCTS.forEach(p => { p.isNew = N.has(p.id); });
})();
/* Bandeau « Nouveauté » en haut à droite de la photo, cartes et fiche produit.
   Trois propositions (laiton, ruban, medaillon) ; BANDEAU_NOUVEAUTE vide = rien
   d'affiché, en attendant le choix de Nathan. Aperçu d'une proposition sur le vrai
   site : ajouter ?bandeau=laiton (ou ruban, medaillon) à l'adresse, gardé le temps
   de la visite ; ?bandeau=aucun pour revenir au site normal. */
const BANDEAU_NOUVEAUTE = '';
function bandeauNouveaute(p) {
  let v = BANDEAU_NOUVEAUTE;
  try {
    const q = new URLSearchParams(location.search).get('bandeau');
    if (q) sessionStorage.setItem('yo_bandeau', q);
    v = q || sessionStorage.getItem('yo_bandeau') || v;
  } catch (e) {}
  if (!p || !p.isNew || !/^(laiton|ruban|medaillon)$/.test(v)) return '';
  return `<span class="bandeau-nouveau b-${v}">${v === 'medaillon' ? 'Nouveau' : 'Nouveauté'}</span>`;
}
window.bandeauNouveaute = bandeauNouveaute;

/* ---------- Nettoyage du bloc « Bientôt » ----------
   Une pièce annoncée « Bientôt » qui est entrée au catalogue ne doit plus y
   figurer, sinon la grille l'afficherait deux fois, une fois avec son prix et
   une fois sans. On compare sur le code de base ET la largeur, parce qu'un
   même modèle peut exister en 25 et pas encore en 40.
   Chargé après coming-soon.js, donc COMING_SOON existe déjà ici. */
if (typeof window.COMING_SOON !== 'undefined' && typeof PRODUCTS !== 'undefined'
    && typeof boucleBase === 'function') {
  const auCatalogue = new Set(
    PRODUCTS.filter(p => p.type === 'boucle')
            .map(p => (boucleBase(p) || '').toUpperCase() + '|' + p.width)
  );
  window.COMING_SOON = window.COMING_SOON.filter(
    x => !auCatalogue.has((x.code || '').toUpperCase() + '|' + x.width)
  );
}

/* ---------- Nom d'affichage des cartes ----------
   Les titres Shopify sont en capitales (BOUCLE N133) et cassaient mal en
   deux lignes. Bas de casse pour les mots, références à chiffres conservées
   telles quelles (N133, LUX976). */
function cardName(name) {
  return String(name || '').split(/\s+/).map(w =>
    /\d/.test(w) ? w.toUpperCase()
                 : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ).join(' ');
}

/* ---------- Carte produit (réutilisée par toutes les grilles) ---------- */
/* Visuels d'une fiche : par identifiant, sinon par modèle et largeur (une même boucle
   peut exister sous deux fiches Shopify, N349/25 par exemple). */
function swatchFor(p) {
  if (!p) return null;
  /* Sans entrée dans boucles-photos.js : pastilles standard lues dans les variantes Shopify. */
  if (typeof BOUCLE_SWATCHES === 'undefined') return swatchesShopify(p);
  const prep = sw => (sw && p.pastilles) ? Object.assign({}, sw, { swatches: avecPastillesShopify(p, sw.swatches) }) : sw;
  if (BOUCLE_SWATCHES[p.id]) return prep(BOUCLE_SWATCHES[p.id]);
  const baseFn = p.type === 'ceinture' ? window.ceintureBase : window.boucleBase;
  if (!baseFn) return null;
  const bs = baseFn(p);
  for (const id in BOUCLE_SWATCHES) {
    const r = PRODUCTS.find(x => x.id === id);
    if (r && r.type === p.type && baseFn(r) === bs && (p.type === 'ceinture' || r.width === p.width)) return prep(BOUCLE_SWATCHES[id]);
  }
  return swatchesShopify(p);
}
/* ============================================================
   Adresse de la fiche d'un produit
   ------------------------------------------------------------
   Sur le thème Shopify, un produit qui existe vraiment dans la
   boutique a DEUX adresses qui rendent exactement la même page :
     /products/<handle>        titre « BOUCLE N20 · Yolète », canonique juste
     /pages/produit?id=<handle> titre générique, canonique sur elle-même
   Le site liait tout vers la seconde : Google voyait alors 200 pages
   au même titre et à la même source HTML, et les classait en
   « explorée, actuellement non indexée ». On envoie donc vers la page
   native dès que le produit existe dans Shopify. La route
   /pages/produit reste nécessaire pour les tiges du composeur, dont
   l'identifiant (tige-preto-black-40) n'est pas un produit Shopify.
   ============================================================ */
function productHref(p) {
  var id = (typeof p === 'string') ? p : (p && p.id);
  if (!id) return '/pages/produit';
  try {
    if (window.Shopify && window.SHOP) {
      var connu = ['boucles40', 'boucles25', 'tiges40', 'tiges25', 'extra', 'sacs'].some(function (k) {
        return (SHOP[k] || []).some(function (x) { return x.handle === id; });
      });
      if (connu) return '/products/' + id;
    }
  } catch (e) {}
  return '/pages/produit?id=' + id;
}

/* Les liens écrits en dur dans le HTML (la guirlande de boucles de l'accueil,
   la mise en avant N27) ne passent pas par productHref. Sur le thème, on les
   réécrit une fois le document chargé, avec la même garde : seul un produit
   présent dans Shopify est envoyé vers sa page native. */
(function reecrireLiensProduit() {
  function passe() {
    if (!window.SHOP) return;
    var liens = document.querySelectorAll('a[href*="/pages/produit?id="], a[href^="/pages/produit?id="]');
    for (var i = 0; i < liens.length; i++) {
      var m = /[?&]id=([^&#]+)/.exec(liens[i].getAttribute('href') || '');
      if (!m) continue;
      var cible = productHref(decodeURIComponent(m[1]));
      if (cible.indexOf('/products/') === 0) liens[i].setAttribute('href', cible);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', passe);
  else passe();
})();

function cardHTML(p) {
  /* Boucles + ceintures : pastilles de coloris CLIQUABLES (aperçu au clic).
     On affiche la PHOTO fond blanc (bien plus propre que le détourage), sauf pour
     les héros de section. */
  const sw = swatchFor(p);
  /* boucle + tige : photo fond blanc affichée en entier (contain), pas rognée/zoomée */
  const isPhoto = p.type === 'boucle' || p.type === 'tige';
  const img = (sw && sw.default_photo) ? sw.default_photo : p.img;
  let swatches = '';
  if (sw && sw.swatches && sw.swatches.length > 1) {   /* picker seulement s'il y a plusieurs coloris */
    /* Boucle : pastille de MATIÈRE (or, argent, bleu, vert…). Une photo de boucle
       réduite à 20px ne montre que du fond blanc, donc on affiche la couleur. */
    /* pastille de matière : couleur calculée depuis le visuel détouré (s.dot),
       sinon table des libellés, sinon la photo en dernier recours. */
    const dots = (p.type === 'boucle' && typeof boucleDotSet === 'function')
      ? boucleDotSet(sw.swatches) : sw.swatches.map(() => null);
    /* Pastille active = la finition de la photo affichée. La photo de la carte est la photo
       principale de la fiche Shopify, qui n'est pas toujours la première variante : la N214
       montrait la bleue avec la pastille rubis active, et « Ajouter » mettait la rubis au
       panier (signalé par Nathan le 26/10/2026, 13 boucles concernées). */
    const sansParam = u => String(u || '').split('?')[0].replace(/^https?:/, '');
    const shown = sw.swatches.slice(0, 6);
    const actif = Math.max(0, shown.findIndex(s => sansParam(s.photo || s.img) === sansParam(img)));
    const items = shown.map((s, i) => {
      const flat = s.dot || dots[i];
      const style = flat ? `background:${flat}` : `background-image:url(${s.dotimg})`;
      return `<button type="button" class="sw${i === actif ? ' active' : ''}${flat ? ' sw-mat' : ''}" data-img="${s.photo || s.img}" data-voption="${s.voption || ''}" style="${style}" title="${s.label}" aria-label="${s.label}"></button>`;
    }).join('');
    const more = sw.swatches.length > 6 ? `<span class="sw-more">+${sw.swatches.length - 6}</span>` : '';
    swatches = `<div class="card-swatches" aria-label="Coloris disponibles">${items}${more}</div>`;
  } else if (p.type === 'tige' && Array.isArray(p.variants) && p.variants.length > 1) {
    /* Tige regroupée par famille : pastilles = les coloris de la famille (aperçu au clic).
       Le zoom photo montre le grain du cuir ; si le coloris n'a pas de photo dédiée
       (Suède), on affiche sa teinte à plat pour rester lisible. */
    const items = p.variants.slice(0, 6).map((v, i) => {
      const style = v.dotColor ? `background:${v.dotColor}` : `background-image:url(${v.img})`;
      return `<button type="button" class="sw${i === 0 ? ' active' : ''}${v.dotColor ? ' sw-mat' : ''}" data-img="${v.img}" style="${style}" title="${v.label}" aria-label="${v.label}"></button>`;
    }).join('');
    const more = p.variants.length > 6 ? `<span class="sw-more">+${p.variants.length - 6}</span>` : '';
    swatches = `<div class="card-swatches" aria-label="Coloris disponibles">${items}${more}</div>`;
  }
  /* Accroche courte au-dessus du nom : la collection pour une boucle, le type sinon. */
  const COLL = (typeof window !== 'undefined' && window.COLLECTION_LABELS) || {};
  const eyebrow = (p.type === 'boucle' && (p.collections || []).length)
    ? (COLL[p.collections[0]] || p.collections[0])
    : (TYPE_LABEL[p.type] || '');

  /* Deux états distincts, qui ne disent pas la même chose au client :
       · bientot -> la pièce n'est pas encore ouverte à la vente (maroquinerie
         en brouillon, ou fiche sans prix). On ne dit ni « Épuisé », qui ferait
         croire à une rupture, ni « 0 € », qui ferait croire à un bug.
       · available === false -> la pièce existe mais le stock est à zéro. */
  const soon = p.bientot === true || !(p.price > 0);
  const out = !soon && p.available === false;
  /* Deux choses différentes : la pastille dit que la pièce n'est pas encore
     en vente, le prix reste affiché dès qu'il existe. Seule une fiche SANS
     prix affiche « Bientôt » à la place du montant, jamais « 0 € ». */
  const prix = (p.price > 0) ? euro(p.price) : '<span class="card-price-soon">Bientôt</span>';
  const tag = soon ? '<span class="card-tag tag-soon">Bientôt</span>'
            : out ? '<span class="card-tag tag-out">Épuisé</span>'
            : p.tag ? `<span class="card-tag">${p.tag}</span>` : '';
  return `
    <a class="card reveal${out ? ' card-out' : ''}${soon ? ' card-soon' : ''}" data-type="${p.type}" href="${productHref(p)}">
      <div class="card-media">
        ${tag}${bandeauNouveaute(p)}
        <img class="${isPhoto ? 'is-photo' : ''}" src="${img}" alt="${p.name}" loading="lazy" />
        ${out || soon ? '' : `<button class="card-add" data-add="${p.id}">Ajouter au panier</button>`}
      </div>
      <div class="card-body">
        ${eyebrow ? `<span class="card-eyebrow">${eyebrow}</span>` : ''}
        <h3>${cardName(p.name)}</h3>
        <div class="card-price">${prix}</div>
        ${swatches}
      </div>
    </a>`;
}

/* ---------- Carte "coming soon" (photo dossier, sans prix ni panier) ---------- */
function soonCardHTML(x) {
  return `
    <div class="card card-soon reveal">
      <div class="card-media">
        <span class="card-tag tag-soon">Bientôt</span>
        <img class="is-photo" src="${x.photo}" alt="${x.name}" loading="lazy" />
      </div>
      <div class="card-body">
        <div><h3>${x.name}</h3><div class="card-cat">Boucle bijou</div></div>
        <div class="card-price card-price-soon">Bientôt</div>
      </div>
    </div>`;
}

/* ---------- Toast ---------- */
let toastTimer;
const toastEl = document.getElementById('toast');
function toast(msg) {
  if (!toastEl) return;
  toastEl.textContent = msg;
  toastEl.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove('show'), 2200);
}

/* ---------- Panier ---------- */
const cart = [];
/* Tailles de tige (tour de taille en cm), les mêmes que sur la fiche produit */
const TAILLES_TIGE = ['75', '80', '85', '90', '95', '100', '105', '110'];
/* Guide des tailles (tour de taille en cm, équivalent vêtement), partagé par la
   fiche produit et le composeur. */
const TAILLES_GUIDE = [
  ['75', '68 à 73', '34 à 36'], ['80', '73 à 78', '36 à 38'], ['85', '78 à 83', '38 à 40'],
  ['90', '83 à 88', '40 à 42'], ['95', '88 à 93', '42 à 44'], ['100', '93 à 98', '44 à 46'],
  ['105', '98 à 103', '46 à 48'], ['110', '103 à 108', '48 à 50'],
];
/* Ajout d'une ligne déjà construite (composeur). Fusion par clé quand elle existe. */
function cartAdd(line) {
  const same = line.key && cart.find(x => x.key === line.key);
  if (same) same.qty += (line.qty || 1);
  else cart.push({ qty: 1, size: null, ...line });
}
const drawer = document.getElementById('drawer');
const overlay = document.getElementById('overlay');
const cartCount = document.getElementById('cartCount');
const drawerItems = document.getElementById('drawerItems');
const drawerFoot = document.getElementById('drawerFoot');
const drawerTotal = document.getElementById('drawerTotal');

function openDrawer() { drawer?.classList.add('open'); overlay?.classList.add('open'); }
function closeDrawer() { drawer?.classList.remove('open'); overlay?.classList.remove('open'); }

document.getElementById('cartBtn')?.addEventListener('click', openDrawer);
document.getElementById('drawerClose')?.addEventListener('click', closeDrawer);
overlay?.addEventListener('click', closeDrawer);
document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

document.body.addEventListener('click', e => {
  /* Pastille de coloris : prévisualise le coloris sur la carte, sans ouvrir la fiche */
  const swatch = e.target.closest('.card-swatches .sw');
  if (swatch) {
    e.preventDefault(); e.stopPropagation();
    const card = swatch.closest('.card');
    const im = card && card.querySelector('.card-media img');
    if (im && swatch.dataset.img) im.src = swatch.dataset.img;
    swatch.closest('.card-swatches').querySelectorAll('.sw').forEach(s => s.classList.toggle('active', s === swatch));
    return;
  }
  /* Ajout avec un coloris précis (cross-sell tige, cartes boutique) */
  const addv = e.target.closest('[data-add-variant]');
  if (addv) {
    e.preventDefault(); e.stopPropagation();
    addToCart(addv.dataset.addVariant, { label: addv.dataset.color || '', img: addv.dataset.img || '' });
    return;
  }
  const add = e.target.closest('[data-add]');
  if (add) {
    e.preventDefault();
    let variant = null;
    const card = add.closest('.card');
    const active = card && card.querySelector('.card-swatches .sw.active');
    if (active) variant = { label: active.getAttribute('aria-label') || '', img: active.dataset.img || '', voption: active.dataset.voption || '' };
    addToCart(add.dataset.add, variant);
  }
  const rm = e.target.closest('[data-remove]');
  if (rm) removeFromCart(+rm.dataset.remove);
});

function addToCart(id, variant) {
  const p = PRODUCTS.find(x => x.id === id);
  if (!p) return;
  const vlabel = (variant && variant.label) ? variant.label : '';
  const key = id + '|' + vlabel;
  const line = cart.find(x => x.key === key);
  const m = vlabel.match(/\bT(\d{2,3})\b/);           /* taille déjà choisie sur la fiche (T85) */
  if (line) line.qty++;
  else cart.push({ ...p, key, qty: 1, vlabel, vimg: (variant && variant.img) || '', size: m ? m[1] : null });
  renderCart();
  toast(`${p.name}${vlabel ? ' · ' + vlabel : ''} ajouté au panier`);
  openDrawer();
}
function removeFromCart(i) { cart.splice(i, 1); renderCart(); }

/* Vignette de panier : objet entièrement détouré et centré (https://cdn.shopify.com/s/files/1/0790/8714/1213/files/cart__,
   générées par scripts/cart_thumbs.py, table dans cart-thumbs.js). À défaut,
   la photo d'origine. */
function cartThumb(src) {
  const T = (typeof window !== 'undefined' && window.CART_THUMBS) || null;
  if (!T || !src) return src;
  /* Les clés de la table portent parfois le ?v= de Shopify, l'image demandée
     parfois un autre : on compare toujours des adresses sans version, sinon la
     vignette détourée est ratée et la photo à fond blanc passe (vu sur la N20). */
  const nu = s => String(s).split('?')[0];
  if (!window.CART_THUMBS_IDX) {
    const I = {}, B = {};
    Object.keys(T).forEach(k => {
      const p = nu(k); I[p] = T[k];
      const b = p.split('/').pop(); if (!B[b]) B[b] = T[k];
    });
    window.CART_THUMBS_IDX = I; window.CART_THUMBS_BASE = B;
  }
  const key = nu(src);
  if (window.CART_THUMBS_IDX[key]) return window.CART_THUMBS_IDX[key];
  /* Shopify renvoie parfois l'image avec un autre préfixe (//cdn…, taille) :
     on retombe sur le nom de fichier. */
  const base = key.split('/').pop().replace(/_(\d+x\d*|\d*x\d+|small|medium|large|grande)(?=\.\w+$)/, '');
  return window.CART_THUMBS_BASE[base] || src;
}

function renderCart() {
  if (!drawerItems) return;
  const count = cart.reduce((s, x) => s + x.qty, 0);
  if (cartCount) { cartCount.textContent = count; cartCount.hidden = count === 0; }

  if (!cart.length) {
    drawerItems.innerHTML = `<div class="drawer-empty">Votre panier est vide.<br>Composez votre première ceinture.</div>`;
    if (drawerFoot) drawerFoot.hidden = true;
    return;
  }
  drawerItems.innerHTML = cart.map((x, i) => `
    <div class="drawer-line">
      <a class="dl-fiche" href="${productHref(x)}"><img class="dl-thumb dl-${x.type}" src="${cartThumb(x.vimg || x.img)}" alt="${x.name}" /></a>

      <div class="dl-info">
        <h4><a class="dl-fiche" href="${productHref(x)}">${x.name}</a>${x.vlabel && !x.name.toLowerCase().includes(x.vlabel.toLowerCase()) ? ` <span class="dl-variant">· ${x.vlabel}</span>` : ''}</h4>
        <div class="dl-cat">${TYPE_LABEL[x.type] || ''} · Qté ${x.qty}</div>
        ${x.type === 'tige' ? `<label class="dl-size-wrap${x.size ? '' : ' dl-size-todo'}"><select class="dl-size" data-size-for="${i}" aria-label="Taille de la tige"><option value="">Votre taille</option>${TAILLES_TIGE.map(s => `<option value="${s}"${x.size === s ? ' selected' : ''}>Taille ${s}</option>`).join('')}</select>${x.size ? '' : '<span class="dl-size-hint">Choisissez votre taille pour valider le panier</span>'}</label>` : ''}
        <div class="dl-price">${euro(x.price * x.qty)}</div>
      </div>
      <button class="dl-remove" data-remove="${i}">Retirer</button>
    </div>`).join('') + crossSell();
  const total = cart.reduce((s, x) => s + x.price * x.qty, 0);
  if (drawerTotal) drawerTotal.textContent = euro(total);
  if (drawerFoot) drawerFoot.hidden = false;
  /* Une tige sans taille bloque la commande */
  const sansTaille = cart.some(x => x.type === 'tige' && !x.size);
  const btn = drawerFoot && drawerFoot.querySelector('.btn');
  const note = drawerFoot && drawerFoot.querySelector('.drawer-note');
  if (btn) btn.disabled = sansTaille;
  if (note) {
    if (!note.dataset.orig) note.dataset.orig = note.textContent;
    note.textContent = sansTaille ? 'Choisissez la taille de chaque tige pour continuer' : note.dataset.orig;
  }
}
document.body.addEventListener('change', e => {
  const s = e.target.closest('.dl-size'); if (!s) return;
  const line = cart[+s.dataset.sizeFor]; if (!line) return;
  line.size = s.value || null;
  renderCart();
});

/* ---------- Vente additionnelle intelligente ----------
   Panier avec boucle(s)  -> proposer des tiges compatibles AVEC choix de couleur.
   Panier avec tige(s)    -> proposer des boucles à associer. */
const CS_COLOR = {
  BLACK: '#23211e', NOIR: '#23211e', BROWN: '#6b4a2b', MARRON: '#6b4a2b', 'DARK BROWN': '#432f1c',
  CAMEL: '#c19a6b', TAUPE: '#8b7e6a', CHERRY: '#7c2230', RUBIS: '#7c2230', NAVY: '#26344d', BLEU: '#2b4a7a',
  IVOIRE: '#efe6d2', BLANC: '#f3efe6', SILVER: '#c6c6ca', ARGENT: '#c6c6ca', CHAMPAGNE: '#e6d4a8',
  ORO: '#caa24a', GOLD: '#caa24a', BOTTOGLIA: '#2f4d3a', PETROLE: '#2b5566', CARDINAL: '#8c2230',
  KAKI: '#6b6b3a', BEIGE: '#d8c6a6', SABLE: '#d8c6a6', MIEL: '#c98a3c', OLIVE: '#6b6b3a', DENIM: '#3b5a7a',
  BRUT: '#9a7b52', EMERAUDE: '#1f6b52', PAPRIKA: '#a5482e', PINK: '#d98aa8', ECORCE: '#6b5238',
  FUSHIA: '#b23a7a', TURQUOISE: '#2fa5a5', GREY: '#8c8c8c', 'LÉOPARD': '#b07a3c', 'COWBOY BLACK': '#3a2a1e', 'COWBOY BROWN': '#6b4a2b',
};
function csColor(n) {
  const u = (n || '').toUpperCase();
  return CS_COLOR[u] || CS_COLOR[u.split(' ')[0]] || '#bcae99';
}

function crossSell() {
  if (typeof PRODUCTS === 'undefined' || !cart.length) return '';
  const types = new Set(cart.map(x => x.type));
  const inCart = new Set(cart.map(x => x.id));
  const widths = new Set(cart.map(x => x.width));

  if (types.has('boucle') && !types.has('tige')) {
    let tiges = PRODUCTS.filter(p => p.type === 'tige' && widths.has(p.width));
    if (tiges.length < 2) tiges = PRODUCTS.filter(p => p.type === 'tige');
    return csTigeBlock('Complétez avec une tige compatible', tiges.slice(0, 2));
  }
  if (types.has('tige') && !types.has('boucle')) {
    let bcl = PRODUCTS.filter(p => p.type === 'boucle' && widths.has(p.width) && !inCart.has(p.id));
    if (bcl.length < 6) bcl = PRODUCTS.filter(p => p.type === 'boucle' && !inCart.has(p.id));
    return csBlock('Choisissez la boucle qui va avec', bcl.slice(0, 6));
  }
  return '';
}

/* Couleur conseillée pour une tige (une seule, versatile) */
function suggestColor(t) {
  const cols = (t.colors || []).map(c => c.name);
  const pref = ['NOIR', 'BLACK', 'MARRON', 'BROWN', 'NAVY', 'TAUPE', 'CARAMEL', 'CAMEL', 'GREY', 'SABLE', 'KAKI', 'OLIVE', 'SILVER', 'ARGENT'];
  for (const key of pref) { const m = cols.find(c => c.toUpperCase().includes(key)); if (m) return m; }
  return cols[0] || '';
}

/* 2 tiges conseillées, chacune avec une couleur suggérée. Clic -> fiche tige (nouvel onglet). */
function csTigeBlock(title, tiges) {
  if (!tiges.length) return '';
  return `
    <div class="cart-cross">
      <p class="cart-cross-title">${title}</p>
      <div class="cc-suggest">
        ${tiges.map(t => {
          const c = suggestColor(t);
          return `<a class="cc-sugg" href="${productHref(t)}" target="_blank" rel="noopener">
            <span class="cc-sugg-media"><img src="${t.img}" alt="${t.name}" loading="lazy" /></span>
            <span class="cc-sugg-body">
              <span class="cc-sugg-name">${t.name}</span>
              ${c ? `<span class="cc-sugg-color"><span class="cc-dot" style="background:${csColor(c)}"></span>${c}</span>` : ''}
              <span class="cc-sugg-meta">${t.width} mm · ${euro(t.price)}</span>
            </span>
            <span class="cc-sugg-go" aria-hidden="true">↗</span>
          </a>`;
        }).join('')}
      </div>
    </div>`;
}

function csBlock(title, items) {
  if (!items.length) return '';
  return `
    <div class="cart-cross">
      <p class="cart-cross-title">${title}</p>
      <div class="cart-cross-row">
        ${items.map(p => `
          <button class="cart-cross-item" data-add="${p.id}" title="Ajouter ${p.name}">
            <span class="ccx-media"><img src="${cartThumb((typeof BOUCLE_SWATCHES !== 'undefined' && BOUCLE_SWATCHES[p.id] && (BOUCLE_SWATCHES[p.id].default_photo || BOUCLE_SWATCHES[p.id].default)) || p.img)}" alt="${p.name}" loading="lazy" /></span>
            <span class="ccx-name">${p.name}</span>
            <span class="ccx-price">${euro(p.price)}</span>
          </button>`).join('')}
      </div>
    </div>`;
}

/* ---------- Header scroll state ----------
   rAF (un seul write DOM par frame) + hystérésis : on passe en « scrolled »
   au-delà de 40px et on ne revient qu'en dessous de 12px. Cette zone morte
   empêche le va-et-vient de la classe (et donc le repaint du header) tout en haut. */
const header = document.getElementById('header');
let headerTicking = false;
addEventListener('scroll', () => {
  if (headerTicking) return;
  headerTicking = true;
  requestAnimationFrame(() => {
    if (scrollY > 40) header?.classList.add('scrolled');
    else if (scrollY < 12) header?.classList.remove('scrolled');
    headerTicking = false;
  });
}, { passive: true });

/* ---------- Mobile menu ---------- */
const burger = document.getElementById('burger');
const navLinks = document.getElementById('navLinks');
burger?.addEventListener('click', () => navLinks?.classList.toggle('open'));
navLinks?.addEventListener('click', e => { if (e.target.tagName === 'A') navLinks.classList.remove('open'); });

/* ---------- Reveal on scroll ---------- */
let io;
function observeReveals() {
  if (!io) {
    io = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } });
    }, { threshold: 0.12 });
  }
  document.querySelectorAll('.reveal:not(.in)').forEach(el => io.observe(el));
}

/* ---------- Init ---------- */
renderCart();
observeReveals();

/* Éditeur de thème (Personnaliser) : une section modifiée est redessinée par Shopify.
   Ses blocs .reveal repartiraient invisibles et sa vidéo arrêtée : on les montre
   tout de suite et on relance la vidéo (10 oct 2026). */
if (window.Shopify && Shopify.designMode) {
  const montrer = racine => {
    if (racine.classList && racine.classList.contains('reveal')) racine.classList.add('in');
    racine.querySelectorAll('.reveal').forEach(el => el.classList.add('in'));
    racine.querySelectorAll('video').forEach(v => { v.muted = true; const p = v.play && v.play(); if (p && p.catch) p.catch(() => {}); });
  };
  montrer(document);
  document.addEventListener('shopify:section:load', e => montrer(e.target));
}

/* ---------- Bandeau de réassurance (demande de la maison, 4 septembre 2026) ----------
   Trois promesses au-dessus du pied de page, sur toutes les pages qui en ont un. */
(function () {
  var footer = document.querySelector('footer.footer');
  if (!footer || document.querySelector('.reassur')) return;
  var ico = {
    contact: '<svg viewBox="0 0 24 24"><path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5z"/><path d="m5 7 7 5.5L19 7"/></svg>',
    port:    '<svg viewBox="0 0 24 24"><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>',
    secure:  '<svg viewBox="0 0 24 24"><rect x="5" y="10.5" width="14" height="9.5" rx="1.5"/><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5"/><circle cx="12" cy="15.3" r="1.1"/></svg>'
  };
  var html =
    '<section class="reassur" aria-label="Nos engagements"><div class="wrap reassur-row">' +
    '<a class="reassur-item" href="mailto:contact@yolete.fr">' + ico.contact + '<span>Contactez-nous</span><small>Réponse sous 24 à 48 h</small></a>' +
    '<a class="reassur-item" href="/pages/livraison-retours">' + ico.port + '<span>Livraison offerte</span><small>Dès ' + (+window.YOLETE_FRANCO || 99) + ' € d’achat, France, Europe et Suisse</small></a>' +
    '<a class="reassur-item" href="/pages/cgv">' + ico.secure + '<span>Paiement sécurisé</span><small>CB, Apple Pay, PayPal, Klarna</small></a>' +
    '</div></section>';
  footer.insertAdjacentHTML('beforebegin', html);
})();

/* ---------- Recherche ----------
   Le bouton de la barre du haut n'ouvrait rien. Il déplie un panneau : on tape,
   les pièces qui correspondent apparaissent, Entrée ouvre la liste complète.
   La page boutique lit déjà ?q= (site et thème Shopify). */
(function () {
  var btn = document.getElementById('searchBtn') || document.querySelector('.icon-btn[aria-label="Rechercher"]');
  if (!btn) return;
  var CIBLE = '/pages/produits';        /* réécrit en /pages/produits dans le thème */
  function fold(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''); }

  var panel = document.createElement('div');
  panel.className = 'search-panel'; panel.id = 'searchPanel'; panel.hidden = true;
  panel.innerHTML =
    '<form class="search-inner" role="search">' +
      '<label class="search-field">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>' +
        '<input type="search" name="q" autocomplete="off" placeholder="Une boucle, un cuir, une référence">' +
      '</label>' +
      '<button type="button" class="search-close" aria-label="Fermer">&times;</button>' +
    '</form>' +
    '<div class="search-results" id="searchResults"></div>';
  (document.querySelector('.header') || document.body).appendChild(panel);

  var input = panel.querySelector('input');
  var results = panel.querySelector('.search-results');
  var form = panel.querySelector('form');

  function ouvrir() {
    panel.hidden = false;
    requestAnimationFrame(function () { panel.classList.add('open'); input.focus(); });
  }
  function fermer() {
    panel.classList.remove('open');
    setTimeout(function () { panel.hidden = true; }, 220);
  }
  btn.addEventListener('click', function (e) { e.preventDefault(); panel.hidden ? ouvrir() : fermer(); });
  panel.querySelector('.search-close').addEventListener('click', fermer);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) fermer(); });

  function lancer(q) {
    if (!q) return;
    location.href = CIBLE + '?q=' + encodeURIComponent(q);
  }
  form.addEventListener('submit', function (e) { e.preventDefault(); lancer(input.value.trim()); });

  /* aperçu des pièces qui correspondent, sans quitter la page */
  var minuteur = null;
  function brancher() {
    var all = results.querySelector('.search-all');
    if (all) all.addEventListener('click', function () { lancer(input.value.trim()); });
  }
  input.addEventListener('input', function () {
    clearTimeout(minuteur);
    minuteur = setTimeout(function () {
      var q = fold(input.value.trim());
      if (q.length < 2 || typeof PRODUCTS === 'undefined') { results.innerHTML = ''; return; }
      var liste = PRODUCTS.filter(function (p) {
        if (p.type === 'boucle' && typeof inFolderBoucle === 'function' && !inFolderBoucle(p)) return false;
        /* Pour une boucle, pas de recherche dans l'identifiant Shopify : il ment sur une
           trentaine de fiches (boucle-n213 = N214, boucle-n379 = N338). Taper « N213 »
           affichait la N214 (signalé par Nathan le 23/09/2026). Seul le vrai nom compte. */
        var dansId = p.type !== 'boucle' && fold(p.id).indexOf(q) !== -1;
        return fold(p.name).indexOf(q) !== -1 || dansId || fold(p.style || '').indexOf(q) !== -1;
      });
      var vus = {}, uniques = [];
      for (var i = 0; i < liste.length && uniques.length < 6; i++) {
        var cle = fold(liste[i].name);
        if (vus[cle]) continue;
        vus[cle] = 1; uniques.push(liste[i]);
      }
      /* L'accueil ne charge que les boucles : l'aperçu y est partiel, la recherche
         complète se fait dans la boutique. On propose donc toujours de la lancer. */
      var bouton = '<button type="button" class="search-all">Voir tous les résultats</button>';
      if (!uniques.length) { results.innerHTML = '<p class="search-empty">Aucun aperçu ici. Lancez la recherche pour voir toute la boutique.</p>' + bouton; brancher(); return; }
      results.innerHTML = uniques.map(function (p) {
        var sw = (typeof swatchFor === 'function') ? swatchFor(p) : null;
        var img = (sw && sw.default_photo) ? sw.default_photo : p.img;
        return '<a class="search-hit" href="' + productHref(p) + '">' +
          '<img src="' + img + '" alt="" loading="lazy">' +
          '<span><b>' + cardName(p.name) + '</b>' + (p.price ? '<i>' + euro(p.price) + '</i>' : '') + '</span></a>';
      }).join('') + bouton;
      brancher();
    }, 160);
  });
})();

/* ---------- Référencement : titre et description de page selon le contenu ---------- */
/* `canonique` : adresse à déclarer à Google quand ce n'est pas l'adresse
   courante. Sert aux fiches produit ouvertes sur /pages/produit?id=<handle>,
   qui rendent la même page que /products/<handle> : sans cela, chaque produit
   existe sous deux adresses et Google n'en indexe aucune des deux. */
function setSEO(title, desc, canonique) {
  document.title = title;
  const set = (sel, attr, val) => { const el = document.querySelector(sel); if (el && val) el.setAttribute(attr, val); };
  set('meta[name="description"]', 'content', desc);
  set('meta[property="og:title"]', 'content', title);
  set('meta[property="og:description"]', 'content', desc);
  const ici = location.origin + location.pathname + location.search.replace(/[?&](debug_analytics|utm_[a-z]+)=[^&]*/g, '').replace(/^&/, '?');
  const url = canonique || ici;
  let c = document.querySelector('link[rel="canonical"]');
  if (!c) { c = document.createElement('link'); c.rel = 'canonical'; document.head.appendChild(c); }
  c.href = url;
  set('meta[property="og:url"]', 'content', url);
}

/* ---------- Consentement cookies (demande de la maison, 4 septembre 2026) ----------
   Aucun cookie non essentiel n'est déposé avant le choix. Le choix est mémorisé
   un an. window.yoleteConsent() renvoie 'accept', 'refuse' ou null, et un
   événement 'yolete:consent' est émis pour brancher une mesure d'audience. */
(function () {
  var CLE = 'yolete_cookies';
  function lire() { try { var v = JSON.parse(localStorage.getItem(CLE) || 'null'); return v && v.choix ? v : null; } catch (e) { return null; } }
  function ecrire(choix) {
    try { localStorage.setItem(CLE, JSON.stringify({ choix: choix, date: new Date().toISOString() })); } catch (e) {}
    document.dispatchEvent(new CustomEvent('yolete:consent', { detail: { choix: choix } }));
  }
  window.yoleteConsent = function () { var v = lire(); return v ? v.choix : null; };
  var deja = lire();
  if (deja && (Date.now() - Date.parse(deja.date)) < 365 * 864e5) return;
  var el = document.createElement('div');
  el.className = 'cookies'; el.setAttribute('role', 'dialog'); el.setAttribute('aria-label', 'Cookies');
  el.innerHTML =
    '<p>Ce site utilise des cookies nécessaires à son fonctionnement et, si vous l’acceptez, des cookies de mesure d’audience. ' +
    'Refuser ne change rien à votre visite. <a href="/pages/cgv#donnees">En savoir plus</a></p>' +
    '<div class="cookies-actions"><button type="button" data-c="refuse">Refuser</button><button type="button" class="ok" data-c="accept">Accepter</button></div>';
  el.addEventListener('click', function (e) {
    var b = e.target.closest('button[data-c]'); if (!b) return;
    ecrire(b.getAttribute('data-c')); el.classList.add('is-out');
    setTimeout(function () { el.remove(); }, 320);
  });
  document.body.appendChild(el);
  requestAnimationFrame(function () { el.classList.add('is-in'); });
})();

/* ---------- Menu au survol de Boucles et Ceintures (validé par Nathan le 1er oct 2026) ----------
   Au survol de Boucles ou de Ceintures dans le bandeau du haut, un panneau sobre, tout en
   texte, en trois colonnes : Largeur (25 mm, 40 mm), Collections ou Cuirs, Sélection.
   Sur téléphone, ces deux entrées se déplient en accordéon dans le menu. Les tuiles 25/40
   du haut de la boutique disparaissent et la largeur devient un filtre (produits.js lit
   window.YOLETE_MENU_SURVOL).
   Les listes Collections et Cuirs se règlent dans Shopify sans toucher au code :
   Boutique en ligne, Navigation, menus « menu-boucles » et « menu-ceintures » (titre et
   lien de chaque entrée, dans l'ordre du menu). Le layout du thème les publie dans
   window.YOLETE_MENUS. Sans ces menus, les listes par défaut ci-dessous s'affichent.
   ?menu=aucun éteint le menu le temps de la visite (comparaison, dépannage). */
(function () {
  var actif = true;
  try {
    var demande = new URLSearchParams(location.search).get('menu');
    if (demande) sessionStorage.setItem('yo_menu', demande);
    if ((demande || sessionStorage.getItem('yo_menu')) === 'aucun') actif = false;
  } catch (e) {}
  window.YOLETE_MENU_SURVOL = actif;
  document.documentElement.classList.toggle('menu-survol', actif);
  if (!actif) return;

  var header = document.getElementById('header');
  var nav = document.getElementById('navLinks');
  if (!header || !nav) return;
  var liens = [].filter.call(nav.children, function (el) { return el.tagName === 'A'; });
  function lienVers(motif) {
    for (var i = 0; i < liens.length; i++) if (motif.test(liens[i].getAttribute('href') || '')) return liens[i];
    return null;
  }
  var entrees = { boucles: lienVers(/type=boucle/), ceintures: lienVers(/type=tige/) };
  if (!entrees.boucles || !entrees.ceintures) return;

  /* Sur la boutique Shopify, une collection vit à /collections/<handle> ; sur la copie de
     travail (Vercel), à collection.html?c=<slug>. */
  var SHOPIFY = !!window.Shopify;
  function versCollection(handle, slug) { return SHOPIFY ? '/collections/' + handle : 'collection.html?c=' + slug; }
  var DEFAUT = {
    boucles: [
      { t: 'L\'Intemporelle', u: versCollection('intemporelles', 'intemporelle') },
      { t: 'Les Bijoux', u: versCollection('bijoux', 'bijou') },
      { t: 'Jungle', u: versCollection('jungle', 'jungle') },
      { t: 'Glitty', u: versCollection('glitter', 'glitter') },
      { t: 'Voglio Bene', u: versCollection('voglio-bene', 'emaillee') }
    ],
    ceintures: [
      { t: 'Preto', u: productHref('tige-preto-black-40') },
      { t: 'Python', u: productHref('tige-python-black-40') },
      { t: 'Vintage', u: productHref('tige-vintage-black-40') },
      { t: 'Vintage Léopard', u: productHref('tige-vintage-leo-black-40') }
    ]
  };
  /* Sans menu « menu-ceintures », les cuirs suivent la collection Shopify « Tiges du
     site » (étiquette tige-40 ou tige-25) : un cuir ajouté par Charlotte y entre seul
     (Savane et Puma Vernis absents du menu, 14 oct 2026). Mêmes clés d'adresse que
     tiges-catalog.js, qui n'est pas chargé sur toutes les pages. */
  function cuirsDuSite() {
    var site = (window.SHOP && window.SHOP.tigesSite) || [];
    var HISTO = { '19840pr-copie': 'preto', 'preto': 'preto', 'python-1': 'python', 'python': 'python',
                  'vintage': 'vintage', 'vintage-25': 'vintage', 'vintage-leopard': 'vintage-leo' };
    var ORDRE = ['preto', 'python', 'vintage', 'vintage-leo'];
    /* noms fixes des cuirs historiques : en anglais, Shopify sert « Preto leather belt » */
    var NOM = { preto: 'Preto', python: 'Python', vintage: 'Vintage', 'vintage-leo': 'Vintage Léopard' };
    var CLE ={ 'BLACK': 'black', 'NOIR': 'black', 'DARK BROWN': 'brown', 'BROWN': 'brown', 'MARRON': 'brown', 'CAMEL': 'camel', 'HAVANE': 'havane' };
    function norm(s) { return String(s || '').trim().toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
    function slug(s) { return norm(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
    function nom(titre) {
      var t = String(titre || '').replace(/^(ceinture|tige)\s+(en\s+)?(cuir\s+)?/i, '')
        .replace(/\s*[·-]?\s*\d{2}\s*mm\b/gi, '').replace(/\s+/g, ' ').trim();
      if (t === t.toUpperCase()) t = t.toLowerCase().replace(/(^|[\s-])\S/g, function (c) { return c.toUpperCase(); });
      return t.replace(/\bLeopard\b/, 'Léopard');
    }
    var par = {}, ordre = [];
    site.forEach(function (p) {
      var tags = p.tags || [];
      var w = tags.indexOf('tige-25') !== -1 ? 25 : 40;
      var cuir = HISTO[p.handle] || slug(nom(p.title)) || p.handle;
      var masques = (p.masques || []).map(norm);
      var coloris = (p.variants || []).map(function (v) { return norm(v.option1); })
        .filter(function (n) { return n && masques.indexOf(n) === -1; });
      if (!coloris.length) return;
      /* le noir d'abord, comme les liens d'origine (tige-preto-black-40). En anglais,
         la traduction automatique de Shopify sert « NOIR » à la place de BLACK. */
      var noir = coloris.filter(function (c) { return CLE[c] === 'black'; })[0];
      var n = noir || coloris[0];
      var e = { t: NOM[cuir] || nom(p.title), u: productHref('tige-' + cuir + '-' + (CLE[n] || slug(n)) + '-' + w), w: w };
      if (!par[cuir]) { par[cuir] = e; ordre.push(cuir); }
      else if (w === 40 && par[cuir].w !== 40) par[cuir] = e;      /* la fiche 40 mm prime */
    });
    ordre.sort(function (a, b) {
      var ia = ORDRE.indexOf(a), ib = ORDRE.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return ordre.map(function (c) { return par[c]; });
  }
  var MENUS = window.YOLETE_MENUS || {};
  function liste(cle, menuShopify) {
    var m = MENUS[menuShopify];
    if (m && m.length) return m;
    if (cle === 'ceintures') {
      var auto = cuirsDuSite();
      if (auto.length) return auto;
    }
    return DEFAUT[cle];
  }
  function ech(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function lien(x) {
    return '<a href="' + ech(x.u) + '">' + ech(x.t) + (x.n ? '<span class="mega-nouveau">Nouveau</span>' : '') + '</a>';
  }
  function colonne(titre, contenu, classe) {
    return '<div class="mega-col' + (classe ? ' ' + classe : '') + '"><span class="mega-titre">' + titre + '</span>' + contenu + '</div>';
  }
  function largeurs(type) {
    return '<div class="mega-liens mega-en-ligne">' +
      '<a href="/pages/produits?type=' + type + '&amp;width=25">25 mm</a>' +
      '<a href="/pages/produits?type=' + type + '&amp;width=40">40 mm</a></div>';
  }
  var collections = liste('boucles', 'menu-boucles');
  var cuirs = liste('ceintures', 'menu-ceintures');
  var CONTENU = {
    boucles:
      colonne('Largeur', largeurs('boucle'), 'mega-col-fixe') +
      colonne('Collections', '<div class="mega-liste">' + collections.map(lien).join('') + '</div>') +
      colonne('Sélection', '<div class="mega-liens">' +
        '<a href="/pages/produits?type=boucle&amp;new=1">Nouveautés</a>' +
        '<a href="/pages/produits?type=boucle&amp;best=1">Best-sellers</a>' +
        '<a href="/pages/produits?type=boucle">Toutes les boucles</a></div>', 'mega-col-fixe'),
    ceintures:
      colonne('Largeur', largeurs('tige'), 'mega-col-fixe') +
      colonne('Cuirs', '<div class="mega-liste">' + cuirs.map(lien).join('') + '</div>') +
      colonne('Sélection', '<div class="mega-liens">' +
        '<a href="/pages/personnalisation">Composer ma ceinture</a>' +
        '<a href="/pages/produits?type=tige">Toutes les ceintures</a></div>', 'mega-col-fixe')
  };

  var panneaux = {};
  Object.keys(entrees).forEach(function (cle) {
    var a = entrees[cle];
    var el = document.createElement('div');
    el.className = 'mega';
    el.id = 'mega-' + cle;
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', a.textContent.trim());
    el.innerHTML = '<div class="wrap mega-in">' + CONTENU[cle] + '</div>';
    header.appendChild(el);
    panneaux[cle] = el;
    a.classList.add('a-mega');
    a.setAttribute('aria-haspopup', 'true');
    a.setAttribute('aria-expanded', 'false');
    a.setAttribute('aria-controls', el.id);
    /* même contenu dans le menu du téléphone, déplié sous l'entrée */
    var sous = document.createElement('div');
    sous.className = 'nav-sous';
    sous.id = 'nav-sous-' + cle;
    sous.innerHTML = CONTENU[cle];
    a.insertAdjacentElement('afterend', sous);
  });
  var voile = document.createElement('div');
  voile.className = 'mega-voile';
  document.body.appendChild(voile);

  /* Ordinateur : ouverture au survol, avec un court délai pour qu'une souris qui
     ne fait que traverser le bandeau n'ouvre rien, et une petite grâce à la sortie. */
  var ecran = window.matchMedia('(min-width: 1041px)');
  var ouvert = null, tOuvre = 0, tFerme = 0, calme = false;
  function montrer(cle) {
    clearTimeout(tOuvre); clearTimeout(tFerme);
    if (cle === ouvert) return;
    ouvert = cle;
    Object.keys(panneaux).forEach(function (k) {
      panneaux[k].classList.toggle('is-open', k === cle);
      entrees[k].classList.toggle('is-actif', k === cle);
      if (ecran.matches) entrees[k].setAttribute('aria-expanded', k === cle ? 'true' : 'false');
    });
    header.classList.toggle('mega-ouvert', !!cle);
    voile.classList.toggle('is-on', !!cle);
  }
  function viser(cle) {
    if (!ecran.matches) return;
    clearTimeout(tOuvre); clearTimeout(tFerme);
    if (ouvert) montrer(cle);
    else tOuvre = setTimeout(function () { montrer(cle); }, 90);
  }
  liens.forEach(function (a) {
    var cle = a === entrees.boucles ? 'boucles' : a === entrees.ceintures ? 'ceintures' : null;
    a.addEventListener('mouseenter', function () { if (cle) viser(cle); else montrer(null); });
    a.addEventListener('focus', function () { if (ecran.matches && !calme) montrer(cle); });
  });
  header.addEventListener('mouseenter', function () { clearTimeout(tFerme); });
  header.addEventListener('mouseleave', function () {
    clearTimeout(tOuvre);
    tFerme = setTimeout(function () { montrer(null); }, 160);
  });
  header.addEventListener('focusout', function (e) { if (!header.contains(e.relatedTarget)) montrer(null); });
  voile.addEventListener('click', function () { montrer(null); });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !ouvert) return;
    var a = entrees[ouvert];
    montrer(null);
    calme = true; a.focus(); calme = false;
  });
  var loupe = document.getElementById('searchBtn');
  if (loupe) loupe.addEventListener('click', function () { montrer(null); });
  function auChangement() { montrer(null); }
  if (ecran.addEventListener) ecran.addEventListener('change', auChangement); else if (ecran.addListener) ecran.addListener(auChangement);

  /* Téléphone et tablette : dans le menu déroulant, Boucles et Ceintures se
     déplient au lieu d'ouvrir la page, une seule à la fois. */
  var petit = window.matchMedia('(max-width: 1040px)');
  Object.keys(entrees).forEach(function (cle) {
    entrees[cle].addEventListener('click', function (e) {
      if (!petit.matches) return;
      e.preventDefault();
      e.stopPropagation();
      var deplier = !entrees[cle].classList.contains('is-deplie');
      Object.keys(entrees).forEach(function (k) {
        var on = deplier && k === cle;
        entrees[k].classList.toggle('is-deplie', on);
        entrees[k].setAttribute('aria-expanded', on ? 'true' : 'false');
        document.getElementById('nav-sous-' + k).classList.toggle('is-open', on);
      });
    });
  });
})();
